import { Button } from "@/components/ui/button";
import { ArrowRight, Play, ShieldCheck, Sparkles } from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";

export default function Hero() {
  const { openLeadCapture } = useLeadCapture();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 bg-dotted opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -left-40 h-[500px] w-[500px] blob-emerald opacity-70" />
      <div className="absolute -top-20 right-0 h-[420px] w-[420px] blob-slate" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 reveal">
            <div
              data-testid="hero-overline"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-700 dark:text-emerald-300"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Sveriges modernaste affärssystem
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] font-bold tracking-tight text-slate-900 dark:text-white"
            >
              Det smarta affärssystemet för{" "}
              <span className="relative inline-block whitespace-nowrap">
                <span className="relative z-10">hundpensionat</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 dark:bg-emerald-500/30 -z-0 rounded" />
              </span>{" "}
              och{" "}
              <span className="relative inline-block whitespace-nowrap">
                <span className="relative z-10">hunddagis</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 dark:bg-emerald-500/30 -z-0 rounded" />
              </span>
              .
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-7 max-w-xl text-lg leading-relaxed text-slate-600 dark:text-slate-300"
            >
              Spara timmar varje vecka med automatiserade bokningar, fakturor och
              digitala hundprofiler — allt på en plats. Byggt av hundmänniskor, för hundmänniskor.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-primary-cta"
                onClick={() => openLeadCapture("Starta gratis testperiod")}
                className="h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)] hover:shadow-[0_10px_28px_-2px_rgba(16,185,129,0.65)] hover:-translate-y-0.5 transition-all group"
              >
                Starta gratis testperiod
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                onClick={() => openLeadCapture("Se hur det fungerar")}
                className="h-12 px-6 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-white font-semibold text-base hover:-translate-y-0.5 transition-all"
              >
                <Play className="mr-2 h-4 w-4 fill-current" />
                Se hur det fungerar
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                Gratis i 14 dagar
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                Inget kreditkort
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                Avbryt när som helst
              </div>
            </div>
          </div>

          {/* Right: image */}
          <div className="lg:col-span-5 relative reveal" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] w-full rounded-[28px] overflow-hidden shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800">
              <img
                src="https://images.pexels.com/photos/37443358/pexels-photo-37443358.jpeg"
                alt="Hundar på dagis"
                className="h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
            </div>

            {/* Floating stat card */}
            <div
              data-testid="hero-floating-card"
              className="absolute -left-4 sm:-left-10 bottom-10 w-56 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold">
                  +42
                </div>
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Aktiva idag</div>
                  <div className="font-display text-lg font-semibold text-slate-900 dark:text-white">
                    Glada hundar
                  </div>
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full w-[85%] rounded-full bg-emerald-500" />
              </div>
            </div>

            <div className="absolute -right-3 top-10 rounded-2xl bg-slate-900 dark:bg-slate-800 text-white px-4 py-3 shadow-xl">
              <div className="text-xs text-slate-400">Faktura skickad</div>
              <div className="font-display font-semibold">4 392 kr</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
